// ─── AUTH GATE + FREEMIUM ──────────────────────────────────────────────────────
// Clerk gère l'auth. Le plan est stocké côté worker (KV), pas dans le JWT.
// Pour activer : REACT_APP_CLERK_PUBLISHABLE_KEY dans test/.env
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useCallback, useRef } from "react";
import { useUser, useAuth, useClerk, SignInButton, SignUpButton, UserButton } from "@clerk/clerk-react";
import { useLanguage } from "./useLanguage";
import { track } from "./track";

const GL = { fontFamily: "'Inter', 'Helvetica Neue', sans-serif" };

const IS_CRUISE_AI   = window.location.hostname.includes('cruiseai.ca');
const CROISIEREAI_EN = 'https://croisiereai.ca/?lang=en';

// ─── ACCÈS ───────────────────────────────────────────────────────────────────
// Modèle : 1 recherche anonyme → inscription → 2 gratuites/mois (renouvelées).
// ⚠️ Vente de packs SUSPENDUE (pivot B2C → B2B, 2026-06-09). Le champ `credits`
// reste lu/affiché pour HONORER les soldes déjà achetés ; on ne vend plus rien.
// usage = { freeUsed, freeLimit, credits, plan, authenticated }

export const PLANS = {
  free:  { label: "Gratuit", color: "#7A9BB5" },
  admin: { label: "Admin",   color: "#E8534A" },
};

// Vigie de prix désactivée au lancement (rebranchée dans un round ultérieur).
// Remettre à true pour réactiver cloche + bouton « Surveiller ce prix » + upsell.
export const VIGIE_ENABLED = false;

// ⚠️ VENTE DE PACKS SUSPENDUE (pivot B2C → B2B, 2026-06-09). On n'affiche ni ne
// vend plus de packs de crédits. La logique de crédits du worker reste intacte
// pour HONORER les packs déjà vendus (solde utilisable, codes-cadeaux, webhook
// Stripe pour tout paiement en cours). Pour réactiver la vente : restaurer le
// tableau PACKS, les cartes d'achat dans PaywallModal et le badge cliquable.

// ─── HOOK USAGE ────────────────────────────────────────────────────────────────

export function useSearchUsage(workerUrl) {
  const { getToken, isSignedIn } = useAuth();
  const { user }                 = useUser();
  const [usage, setUsage]        = useState(null); // { freeUsed, freeLimit, credits, plan, authenticated }

  const fetchUsage = useCallback(async () => {
    if (!isSignedIn) return;
    try {
      const token = await getToken();
      const email = user?.primaryEmailAddress?.emailAddress || "";
      const lang  = window.location.hostname.includes("cruiseai") ? "en" : "fr";
      const res   = await fetch(workerUrl, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ action: "usage", token, email, lang }),
      });
      if (res.ok) setUsage(await res.json());
    } catch {}
  }, [isSignedIn, getToken, workerUrl, user]);

  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  // Refresh automatique quand l'utilisateur revient sur l'onglet (ex: après paiement Stripe)
  useEffect(() => {
    const onFocus = () => { if (isSignedIn) fetchUsage(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isSignedIn, fetchUsage]);

  return { usage, refreshUsage: fetchUsage, getToken, isSignedIn };
}

// ─── HEADER AUTH BUTTON ────────────────────────────────────────────────────────

export function AuthButton({ usage, onBeforeAuth }) {
  const { isSignedIn, isLoaded } = useUser();
  const { lang } = useLanguage();
  const { redirectToSignIn } = useClerk();
  const isEN = lang === "en";

  if (!isLoaded && !IS_CRUISE_AI) return null;

  const isAdmin       = usage?.plan === "admin";
  const freeRemaining = usage ? Math.max(0, (usage.freeLimit ?? 2) - (usage.freeUsed ?? 0)) : 0;
  const credits       = usage?.credits ?? 0;
  const totalLeft     = freeRemaining + credits;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {isSignedIn ? (
        <>
          {usage && (
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {/* Badge solde — affichage seul (vente de packs suspendue, cf. note en tête). */}
              <span
                title={isEN ? "Your searches" : "Vos recherches"}
                style={{ fontSize: 13, fontWeight: 700, color: isAdmin ? "#E8534A" : (totalLeft > 0 ? "#C9A84C" : "#E8534A"), background: "rgba(201,168,76,0.10)", border: "1px solid #C9A84C44", borderRadius: 8, padding: "3px 9px", ...GL, letterSpacing: "0.03em" }}
              >
                {isAdmin
                  ? "Admin ✓"
                  : (isEN
                      ? `${totalLeft} search${totalLeft === 1 ? "" : "es"}${credits > 0 ? " ⚡" : ""}`
                      : `${totalLeft} recherche${totalLeft === 1 ? "" : "s"}${credits > 0 ? " ⚡" : ""}`)}
              </span>
            </div>
          )}
          <UserButton afterSignOutUrl="/" appearance={{ elements: { avatarBox: { width: 32, height: 32 } } }} />
        </>
      ) : IS_CRUISE_AI ? (
        <a href={CROISIEREAI_EN} style={{ textDecoration: "none" }}>
          <button style={{ padding: "7px 16px", borderRadius: 10, background: "#C9A84C", border: "none", color: "#07111F", fontSize: 14, cursor: "pointer", fontWeight: 700, ...GL }}>
            Sign in →
          </button>
        </a>
      ) : (
        <button
          onClick={() => { onBeforeAuth?.(); redirectToSignIn({ redirectUrl: window.location.href }); }}
          style={{ padding: "7px 16px", borderRadius: 10, background: "#C9A84C", border: "none", color: "#07111F", fontSize: 14, cursor: "pointer", fontWeight: 700, ...GL }}
        >
          {isEN ? "Sign in →" : "Se connecter →"}
        </button>
      )}
    </div>
  );
}

// ─── PAYWALL MODAL ─────────────────────────────────────────────────────────────

export function PaywallModal({ onClose, reason = "limit", onBeforeAuth, onEnterCode }) {
  const { isSignedIn } = useUser();
  const { lang } = useLanguage();
  const isEN = lang === "en";

  // Track Vercel Analytics : combien voient le mur, et pour quelle raison
  // (anon = 1ère recherche épuisée · limit = quota perso · budget = plafond
  // mensuel global atteint · plans = ouverture manuelle).
  useEffect(() => { track("paywall_shown", { reason }); }, [reason]);

  // ⚠️ Vente de packs suspendue (pivot B2C → B2B). Ce mur est désormais
  // purement informatif : il annonce l'épuisement du quota gratuit (renouvelé
  // chaque mois) et propose l'inscription gratuite + la saisie d'un code-cadeau.

  const titleMap = {
    limit: isEN ? "Out of searches"                    : "Recherches épuisées",
    anon:  isEN ? "Your free search is used up"         : "Votre recherche gratuite est épuisée",
    budget: isEN ? "Free searches used up this month"   : "Recherches gratuites épuisées ce mois-ci",
    plans: isEN ? "Your searches"                       : "Vos recherches",
  };
  const subtitleMap = {
    limit: isEN ? "You've used all your free searches. Your quota renews at the start of next month." : "Vous avez utilisé toutes vos recherches gratuites. Votre quota se renouvelle au début du mois prochain.",
    anon:  isEN ? "Each search costs us real AI fees. Sign up free for 2 searches every month." : "Chaque recherche génère de vrais frais IA. Inscrivez-vous gratuitement pour 2 recherches chaque mois.",
    budget: isEN
      ? "This month's free-search quota is reached. It renews at the start of next month."
      : "Le quota mensuel de recherches gratuites est atteint. Il se renouvelle au début du mois prochain.",
    plans: "",
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 16 }}>
      <div style={{ background: "#07111F", border: "1px solid #C9A84C33", borderRadius: 20, maxWidth: 680, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 32 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
          <div>
            <h2 style={{ color: "#C9A84C", margin: 0, fontSize: 22, ...GL }}>{titleMap[reason] || titleMap.plans}</h2>
            <p style={{ color: "#7A9BB5", margin: "6px 0 0", fontSize: 15, ...GL }}>{subtitleMap[reason]}</p>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#7A9BB5", fontSize: 20, cursor: "pointer", padding: 4 }}>✕</button>
        </div>

        {!isSignedIn && reason === "anon" && (
          <div style={{ background: "rgba(201,168,76,0.08)", border: "1px solid #C9A84C33", borderRadius: 12, padding: 16, marginBottom: 20, textAlign: "center" }}>
            <p style={{ color: "#C8D8E8", margin: 0, fontSize: 15, ...GL, lineHeight: 1.6 }}>
              💡 {isEN
                ? "Each search costs us real AI fees — that's why the first search is free, then a quick free sign-up unlocks more."
                : "Chaque recherche génère de vrais frais IA — c'est pourquoi la première recherche est gratuite, puis une inscription gratuite rapide débloque la suite."}
            </p>
          </div>
        )}

        {!isSignedIn && reason !== "anon" && (
          <div style={{ background: "rgba(201,168,76,0.08)", border: "1px solid #C9A84C33", borderRadius: 12, padding: 16, marginBottom: 20, textAlign: "center" }}>
            <p style={{ color: "#C8D8E8", margin: "0 0 12px", fontSize: 15, ...GL }}>
              {IS_CRUISE_AI
                ? (isEN ? "Free · No credit card · 2 searches/month included" : "Gratuit · Aucune carte requise · 2 recherches/mois incluses")
                : (isEN ? "Create a free account to manage your searches." : "Créez un compte gratuit pour gérer vos recherches.")}
            </p>
            {IS_CRUISE_AI ? (
              <>
                <button onClick={() => { onBeforeAuth?.(); window.location.href = CROISIEREAI_EN; }} style={{ padding: "8px 20px", borderRadius: 10, background: "#C9A84C", border: "none", color: "#07111F", fontSize: 15, fontWeight: 700, cursor: "pointer", ...GL }}>
                  Create free account →
                </button>
                <div style={{ marginTop: 10 }}>
                  <button onClick={() => { onBeforeAuth?.(); window.location.href = CROISIEREAI_EN; }} style={{ background: "none", border: "none", color: "#7A9BB5", fontSize: 14, cursor: "pointer", textDecoration: "underline", ...GL }}>
                    Already have an account? Sign in
                  </button>
                </div>
              </>
            ) : (
              <>
                <SignUpButton mode="redirect" afterSignUpUrl={window.location.href} afterSignInUrl={window.location.href}>
                  <button onClick={() => onBeforeAuth?.()} style={{ padding: "8px 20px", borderRadius: 10, background: "#C9A84C", border: "none", color: "#07111F", fontSize: 15, fontWeight: 700, cursor: "pointer", ...GL }}>
                    {isEN ? "Create free account →" : "Créer un compte gratuit →"}
                  </button>
                </SignUpButton>
                <div style={{ marginTop: 10 }}>
                  <SignInButton mode="redirect" afterSignInUrl={window.location.href}>
                    <button onClick={() => onBeforeAuth?.()} style={{ background: "none", border: "none", color: "#7A9BB5", fontSize: 14, cursor: "pointer", textDecoration: "underline", ...GL }}>
                      {isEN ? "Already have an account? Sign in" : "Déjà un compte ? Se connecter"}
                    </button>
                  </SignInButton>
                </div>
              </>
            )}
          </div>
        )}

        {onEnterCode && (
          <p style={{ textAlign: "center", marginTop: 8, ...GL }}>
            <button onClick={() => { onClose(); onEnterCode(); }} style={{ background: "none", border: "none", color: "#C9A84C", fontSize: 15, cursor: "pointer", textDecoration: "underline", padding: 0, ...GL }}>
              {isEN ? "🎁 I have a code" : "🎁 J'ai un code"}
            </button>
          </p>
        )}
      </div>
    </div>
  );
}


// ─── GATE: bloque la recherche si quota dépassé ────────────────────────────────

export function useSearchGate(usage, onBlocked, isSignedIn) {
  return useCallback(() => {
    if (!isSignedIn) return true; // anon géré en amont par anonSearchCount dans search()
    if (!usage) return true; // connecté mais usage pas encore chargé → laisser passer
    if (usage.plan === "admin") return true;
    const freeLeft = Math.max(0, (usage.freeLimit ?? 2) - (usage.freeUsed ?? 0));
    if (freeLeft + (usage.credits ?? 0) <= 0) {
      onBlocked("limit");
      return false;
    }
    return true;
  }, [usage, onBlocked, isSignedIn]);
}

// ─── ADMIN — Croisière du Mois ─────────────────────────────────────────────────

// Bloc post Facebook prêt-à-coller (texte + liens affiliés) avec bouton Copier.
function FBPostBlock({ post }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(post.text); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };
  return (
    <div style={{ background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 12, padding: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ color: "#C9A84C", fontSize: 12, fontWeight: 700, ...GL }}>{post.header} — post Facebook</span>
        <button onClick={copy} style={{ padding: "6px 14px", borderRadius: 8, background: copied ? "#5CB85C" : "#C9A84C", border: "none", color: "#07111F", fontSize: 13, fontWeight: 700, cursor: "pointer", ...GL }}>
          {copied ? "✓ Copié !" : "📋 Copier"}
        </button>
      </div>
      <textarea readOnly value={post.text} onFocus={e => e.target.select()} style={{ width: "100%", minHeight: 210, background: "#07111F", border: "1px solid #1A2F45", borderRadius: 8, padding: "10px 12px", color: "#C8D8E8", fontSize: 12.5, lineHeight: 1.55, ...GL, boxSizing: "border-box", resize: "vertical" }} />
    </div>
  );
}

export function AdminCDMModal({ onClose, onGenerate, onSend, loading, sending, offers, result, vigie, setVigie, fbPosts }) {
  const offerCard = (label, o, isAdventure) => (
    <div style={{ background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 12, padding: 16, marginBottom: 12 }}>
      <p style={{ color: "#C9A84C", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em", margin: "0 0 8px", ...GL }}>{label}</p>
      <p style={{ color: "#E8DCC8", fontSize: 15, fontWeight: 700, margin: "0 0 2px", ...GL }}>{o.ship_name} <span style={{ color: "#7A9BB5", fontSize: 14, fontWeight: 400 }}>— {o.cruise_line}</span></p>
      <p style={{ color: "#7A9BB5", fontSize: 13, margin: "0 0 8px", ...GL }}>{o.duration_days} jours · {o.departure_port} · {o.departure_date}</p>
      <p style={{ color: "#A8C0D0", fontSize: 14, lineHeight: 1.5, margin: "0 0 8px", ...GL }}>{o.itinerary}</p>
      <p style={{ color: "#C9A84C", fontSize: 18, fontWeight: 700, margin: "0 0 6px", ...GL }}>{Number(o.price_per_person_cad).toLocaleString("fr-CA")} $ CAD / personne</p>
      <p style={{ color: "#C8D8E8", fontSize: 14, fontStyle: "italic", lineHeight: 1.5, margin: "0 0 10px", ...GL }}>{o.wow_factor}</p>
      {isAdventure && <p style={{ color: "#7A9BB5", fontSize: 13, margin: 0, ...GL }}>Deal : {o.has_deal ? "✅ Oui" : "❌ Non — vigie suggérée"}</p>}
      <a href={o.booking_url} target="_blank" rel="noopener noreferrer" style={{ color: "#C9A84C", fontSize: 13, ...GL }}>🔗 {o.booking_url?.slice(0, 60)}…</a>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, padding: 16 }}>
      <div style={{ background: "#07111F", border: "1px solid #C9A84C33", borderRadius: 20, maxWidth: 640, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ color: "#C9A84C", margin: 0, fontSize: 18, ...GL }}>⚙️ Croisière du Mois — Admin</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#7A9BB5", fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        {!offers && !loading && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <p style={{ color: "#7A9BB5", fontSize: 15, marginBottom: 20, ...GL }}>Génère 2 offres exceptionnelles via Claude (Sonnet + web search).<br/>1 Deal classique + 1 Aventure bucket-list.</p>
            <button onClick={onGenerate} style={{ padding: "12px 28px", borderRadius: 10, background: "#C9A84C", border: "none", color: "#07111F", fontSize: 14, fontWeight: 700, cursor: "pointer", ...GL }}>
              🔍 Générer Croisière du Mois
            </button>
          </div>
        )}

        {loading && (
          <div style={{ textAlign: "center", padding: "32px 0" }}>
            <p style={{ color: "#C9A84C", fontSize: 15, ...GL }}>⏳ Claude cherche les meilleures offres… (30-60 sec)</p>
          </div>
        )}

        {offers && !result && (
          <>
            {offerCard("💰 LE DEAL", offers.deal, false)}
            {offerCard("🌍 L'AVENTURE", offers.adventure, true)}

            {fbPosts && (
              <div style={{ marginBottom: 18, borderTop: "1px solid #1A2F45", paddingTop: 16 }}>
                <p style={{ color: "#E8DCC8", fontSize: 14, fontWeight: 700, margin: "0 0 4px", ...GL }}>📣 Posts Facebook prêts-à-coller</p>
                <p style={{ color: "#7A9BB5", fontSize: 12, margin: "0 0 12px", ...GL }}>Liens affiliés inclus (CruiseDirect + vol + hôtel Expedia). Copie, colle sur Facebook, ajuste si tu veux.</p>
                <FBPostBlock post={fbPosts.deal} />
                <FBPostBlock post={fbPosts.adventure} />
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <p style={{ color: "#A8C0D0", fontSize: 14, marginBottom: 6, ...GL }}>💬 Témoignage vigie (optionnel)</p>
              <input
                value={vigie}
                onChange={e => setVigie(e.target.value)}
                placeholder='Ex: "Marie a sauvé 340$ sur sa croisière grâce à la vigie !"'
                style={{ width: "100%", background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 8, padding: "10px 12px", color: "#E8DCC8", fontSize: 14, ...GL, boxSizing: "border-box" }}
              />
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={onGenerate} style={{ flex: 1, padding: "10px", borderRadius: 10, background: "transparent", border: "1px solid #1A2F45", color: "#7A9BB5", fontSize: 14, cursor: "pointer", ...GL }}>
                🔄 Régénérer
              </button>
              <button onClick={onSend} disabled={sending} style={{ flex: 2, padding: "10px", borderRadius: 10, background: sending ? "#5A7A95" : "#C9A84C", border: "none", color: "#07111F", fontSize: 15, fontWeight: 700, cursor: sending ? "default" : "pointer", ...GL }}>
                {sending ? "⏳ Envoi en cours…" : "✅ Approuver & Envoyer"}
              </button>
            </div>
          </>
        )}

        {result && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            {result.success
              ? <><p style={{ color: "#5CB85C", fontSize: 16, fontWeight: 700, ...GL }}>✅ Emails envoyés !</p>
                  <p style={{ color: "#A8C0D0", fontSize: 15, ...GL }}>{result.sent} emails — {result.pro} Pro · {result.free} Gratuit/Explorateur</p></>
              : <p style={{ color: "#E8534A", fontSize: 15, ...GL }}>❌ Erreur : {result.error}</p>
            }
            <button onClick={() => { onClose(); }} style={{ marginTop: 16, padding: "10px 24px", borderRadius: 10, background: "#C9A84C", border: "none", color: "#07111F", fontSize: 15, fontWeight: 700, cursor: "pointer", ...GL }}>Fermer</button>
          </div>
        )}
      </div>
    </div>
  );
}


// ─── REDEEM: réclamer un code testeur (usage unique) ───────────────────────────

export function RedeemCodeModal({ workerUrl, onClose, onRedeemed }) {
  const { getToken } = useAuth();
  const { lang } = useLanguage();
  const isEN = lang === "en";
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState(null); // { ok:bool, text:string }

  async function submit() {
    const c = code.trim();
    if (!c || busy) return;
    setBusy(true); setMsg(null);
    try {
      const token = await getToken();
      const res = await fetch(workerUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "redeem_code", code: c, token }),
      });
      const json = await res.json();
      if (json.ok) {
        setMsg({ ok: true, text: isEN
          ? `✅ ${json.credited} searches added to your account!`
          : `✅ ${json.credited} recherches ajoutées à votre compte !` });
        onRedeemed?.();
        setTimeout(() => onClose(), 1800);
      } else if (json.reason === "used") {
        setMsg({ ok: false, text: isEN ? "This code has already been used." : "Ce code a déjà été utilisé." });
      } else {
        setMsg({ ok: false, text: isEN ? "Invalid code." : "Code invalide." });
      }
    } catch {
      setMsg({ ok: false, text: isEN ? "Connection error — please try again." : "Erreur de connexion — réessayez." });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 210, padding: 16 }}>
      <div style={{ background: "#07111F", border: "1px solid #C9A84C33", borderRadius: 20, maxWidth: 400, width: "100%", padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
          <h2 style={{ color: "#C9A84C", margin: 0, fontSize: 20, ...GL }}>{isEN ? "🎁 Enter your code" : "🎁 Entrez votre code"}</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#7A9BB5", fontSize: 20, cursor: "pointer", padding: 4 }}>✕</button>
        </div>
        <p style={{ color: "#7A9BB5", fontSize: 15, margin: "0 0 16px", ...GL }}>
          {isEN ? "Add free searches to your account." : "Ajoutez des recherches gratuites à votre compte."}
        </p>
        <input
          autoFocus
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          onKeyDown={e => { if (e.key === "Enter") submit(); }}
          placeholder="CAI-XXXXXXXX"
          disabled={busy}
          style={{ width: "100%", boxSizing: "border-box", background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 10, padding: "11px 14px", color: "#C8D8E8", fontSize: 14, letterSpacing: "0.06em", outline: "none", ...GL }}
        />
        {msg && (
          <p style={{ color: msg.ok ? "#5CB85C" : "#E8534A", fontSize: 15, margin: "12px 0 0", ...GL }}>{msg.text}</p>
        )}
        <button onClick={submit} disabled={busy || !code.trim()} style={{
          marginTop: 16, width: "100%", padding: "12px", borderRadius: 10, border: "none",
          background: busy || !code.trim() ? "#1A2F45" : "linear-gradient(135deg,#C9A84C,#A8882A)",
          color: busy || !code.trim() ? "#3A5A72" : "#07111F", fontSize: 14, fontWeight: 700,
          cursor: busy || !code.trim() ? "default" : "pointer", ...GL,
        }}>
          {busy ? (isEN ? "Checking…" : "Vérification…") : (isEN ? "Apply code" : "Appliquer le code")}
        </button>
      </div>
    </div>
  );
}


// ─── ADMIN: tableau de bord funnel (Analytics Engine) ──────────────────────────

// Tableau de bord VISUEL in-app (graphiques) — générable depuis l'admin pendant un meeting.
// Charge Chart.js depuis le CDN (admin only). Données via le worker (funnel + stats), max 90 j
// (rétention Analytics Engine — pas de fenêtre « 1 an » possible).
export function AdminDashboardModal({ workerUrl, onClose }) {
  const { getToken } = useAuth();
  const [days, setDays] = useState(30);
  const [d, setD]       = useState(null);
  const [err, setErr]   = useState(null);
  const [busy, setBusy] = useState(true);
  const [libReady, setLibReady] = useState(typeof window !== "undefined" && !!window.Chart);
  const charts = useRef([]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.Chart) { setLibReady(true); return; }
    const s = document.createElement("script");
    s.src = "https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js";
    s.onload  = () => setLibReady(true);
    s.onerror = () => setErr("Chargement des graphiques échoué (connexion ?).");
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    let cancel = false; setBusy(true); setErr(null);
    (async () => {
      try {
        const token = await getToken();
        const post = (action) => fetch(workerUrl + "?days=" + days, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action, token }),
        }).then(r => r.json());
        const [funnel, stats] = await Promise.all([post("funnel"), post("stats")]);
        if (cancel) return;
        if (!funnel.ok && !stats.ok) { setErr("Échec du chargement."); setBusy(false); return; }
        const tot = {}; (stats.totals || []).forEach(r => tot[r.event] = Number(r.n) || 0);
        const daily = (stats.daily || []).map(r => ({ day: String(r.day).slice(0, 10), n: Number(r.n) || 0 })).sort((a, b) => a.day.localeCompare(b.day));
        const ACQL = { welcome_modal_shown: "Accueil vu", welcome_funnel_click: "« Commencer »", search: "Recherche", search_result: "Résultats vus", offer_clicked: "Clic offre" };
        const acq = ["welcome_modal_shown", "welcome_funnel_click", "search", "search_result", "offer_clicked"]
          .map(e => ({ label: ACQL[e], n: tot[e] || (e === "search" ? (funnel.overall?.searches_tracked || 0) : e === "offer_clicked" ? (funnel.overall?.offer_clicks || 0) : 0) }));
        setD({ o: funnel.overall || {}, regions: funnel.by_region || [], daily, acq });
        setBusy(false);
      } catch { if (!cancel) { setErr("Erreur de connexion."); setBusy(false); } }
    })();
    return () => { cancel = true; };
  }, [workerUrl, getToken, days]);

  useEffect(() => {
    if (!libReady || !d || !window.Chart) return;
    const C = window.Chart;
    charts.current.forEach(c => { try { c.destroy(); } catch {} }); charts.current = [];
    const GOLD = "#D8A03D", SLATE = "#6B7A8C", GREEN = "#7FB873", GOLDL = "#F0BC58";
    C.defaults.color = "#9A9890"; C.defaults.font.family = "'IBM Plex Sans',sans-serif"; C.defaults.font.size = 11;
    const grid = { color: "rgba(237,234,226,.06)" };
    const mk = (id, cfg) => { const el = document.getElementById(id); if (el) charts.current.push(new C(el, cfg)); };
    if (d.daily.length) mk("ad_daily", { type: "line", data: { labels: d.daily.map(x => x.day.slice(5)), datasets: [{ data: d.daily.map(x => x.n), borderColor: GOLD, backgroundColor: "rgba(216,160,61,.12)", fill: true, tension: .3, pointRadius: 2 }] }, options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, grid, ticks: { precision: 0 } }, x: { grid: { display: false } } } } });
    if (d.acq.some(a => a.n > 0)) mk("ad_acq", { type: "bar", data: { labels: d.acq.map(a => a.label), datasets: [{ data: d.acq.map(a => a.n), backgroundColor: [SLATE, "#8A6E2A", GOLD, GOLDL, GREEN] }] }, options: { indexAxis: "y", responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, grid, ticks: { precision: 0 } }, y: { grid: { display: false } } } } });
    if (d.regions.length) mk("ad_reg", { type: "bar", data: { labels: d.regions.map(r => r.region), datasets: [{ data: d.regions.map(r => r.searches), backgroundColor: SLATE }] }, options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, grid, ticks: { precision: 0 } }, x: { grid: { display: false } } } } });
    return () => { charts.current.forEach(c => { try { c.destroy(); } catch {} }); charts.current = []; };
  }, [libReady, d]);

  const o = d?.o || {};
  const heures = Math.round((o.searches_tracked || 0) * 1.5);
  const heuresFmt = o.searches_tracked ? (heures >= 1000 ? (heures / 1000).toFixed(1).replace(".", ",") + " k h" : "≈ " + heures + " h") : "—";
  const KPI = [
    [o.searches_tracked ?? "—", "Recherches"],
    [heuresFmt, "Temps de défrichage sauvé (est.)"],
    [(o.click_per_search_pct ?? 0) + "%", "Conversion (clic/recherche)"],
    [o.offer_clicks ?? "—", "Clics vers réservation"],
    [(o.empty_pct ?? 0) + "%", "Recherches vides"],
    [(o.served_from_cache_pct ?? 0) + "%", "Servi du cache"],
  ];
  const card = { background: "#121316", border: "1px solid rgba(237,234,226,.09)", borderRadius: 14 };
  const cardH = { fontFamily: "'Playfair Display',serif", fontSize: 17, color: "#EDEAE2", fontWeight: 700, margin: "0 0 10px" };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 220, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "radial-gradient(ellipse 90% 50% at 50% -8%,rgba(216,160,61,.06),transparent 60%),#0C0D0F", border: "1px solid rgba(216,160,61,.2)", borderRadius: 18, maxWidth: 900, width: "100%", maxHeight: "92vh", overflowY: "auto", padding: 26 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: ".4em", textTransform: "uppercase", color: "#D8A03D", marginBottom: 6 }}>Croisière AI</div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", color: "#EDEAE2", margin: 0, fontSize: 26, fontWeight: 700 }}>Tableau de bord</h2>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {[30, 90].map(x => (
              <button key={x} onClick={() => setDays(x)} disabled={busy}
                style={{ fontSize: 12, letterSpacing: ".06em", textTransform: "uppercase", borderRadius: 16, padding: "6px 14px", cursor: busy ? "default" : "pointer",
                  color: x === days ? "#0C0D0F" : "#9A9890", background: x === days ? "#D8A03D" : "transparent",
                  border: "1px solid " + (x === days ? "#D8A03D" : "rgba(237,234,226,.12)"), fontWeight: x === days ? 700 : 400 }}>
                {x === 90 ? "90 j (max)" : x + " j"}
              </button>
            ))}
            <button onClick={onClose} style={{ background: "none", border: "none", color: "#9A9890", fontSize: 22, cursor: "pointer", padding: 4, marginLeft: 4 }}>✕</button>
          </div>
        </div>

        {err && <p style={{ color: "#C5524A", fontSize: 14 }}>❌ {err}</p>}
        {busy && <p style={{ color: "#9A9890", fontSize: 14 }}>Chargement…</p>}

        {d && !busy && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 18 }}>
              {KPI.map(([v, l], i) => (
                <div key={i} style={{ ...card, padding: "16px 18px", ...(i < 2 ? { borderColor: "rgba(216,160,61,.35)", background: "linear-gradient(160deg,rgba(216,160,61,.08),#121316)" } : {}) }}>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 30, fontWeight: 700, color: "#F0BC58", lineHeight: 1 }}>{v}</div>
                  <div style={{ fontSize: 11, color: "#9A9890", marginTop: 7 }}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{ ...card, padding: "18px 20px", marginBottom: 14 }}>
              <h3 style={cardH}>Recherches par jour</h3>
              <div style={{ position: "relative", height: 220 }}><canvas id="ad_daily" /></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div style={{ ...card, padding: "18px 20px" }}>
                <h3 style={cardH}>Entonnoir d'acquisition</h3>
                <div style={{ position: "relative", height: 220 }}><canvas id="ad_acq" /></div>
              </div>
              <div style={{ ...card, padding: "18px 20px" }}>
                <h3 style={cardH}>Destinations les plus recherchées</h3>
                <div style={{ position: "relative", height: 220 }}><canvas id="ad_reg" /></div>
              </div>
            </div>
            <p style={{ fontSize: 11, color: "#605E56", marginTop: 14, textAlign: "center" }}>Fenêtre {days} jours · données en direct · vrais utilisateurs (admin/test exclus) · max 90 j (rétention Analytics Engine)</p>
          </>
        )}
      </div>
    </div>
  );
}

export function AdminStatsModal({ workerUrl, onClose }) {
  const { getToken } = useAuth();
  const [data, setData] = useState(null);
  const [cov, setCov]   = useState(null);   // couverture du cache de voilages
  const [err, setErr]   = useState(null);
  const [busy, setBusy] = useState(true);
  const [days, setDays] = useState(30);     // fenêtre : 14 / 30 / 90 jours

  useEffect(() => {
    let cancel = false;
    setBusy(true); setErr(null);
    (async () => {
      try {
        const token = await getToken();
        const post = (action) => fetch(workerUrl + "?days=" + days, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action, token }),
        }).then(r => r.json());
        const [statsJson, covJson] = await Promise.all([post("stats"), post("cache_coverage")]);
        if (cancel) return;
        if (statsJson.ok) setData(statsJson);
        else setErr(statsJson.error?.message || "Échec.");
        if (covJson.ok) setCov(covJson);
      } catch { if (!cancel) setErr("Erreur de connexion."); }
      finally { if (!cancel) setBusy(false); }
    })();
    return () => { cancel = true; };
  }, [workerUrl, getToken, days]);

  const totals = data?.totals || [];
  const daily  = data?.daily  || [];
  const n = (ev) => { const r = totals.find(t => t.event === ev); return r ? Math.round(Number(r.n)) : 0; };

  // Étapes clés de l'entonnoir, dans l'ordre du parcours.
  const FUNNEL = [
    ["welcome_modal_shown",   "👀 Modal d'accueil vu"],
    ["welcome_modal_closed",  "✓ Modal d'accueil fermé"],
    ["walkthrough_started",   "🧭 Tour guidé démarré"],
    ["walkthrough_completed", "🏁 Tour guidé complété"],
    ["welcome_funnel_click",  "📋 Clic vers le formulaire"],
    ["search",                "🔍 Recherches lancées"],
    ["offer_clicked",         "💰 Clics sur une offre"],
    ["save_signup_cta",       "📧 CTA inscription (sauvegarde)"],
  ];
  const lab = { fontSize: 13, color: "#7A9BB5", ...GL };
  const num = { fontSize: 14, color: "#E8DCC8", fontWeight: 700, ...GL };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 210, padding: 16 }}>
      <div style={{ background: "#07111F", border: "1px solid #C9A84C33", borderRadius: 20, maxWidth: 520, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <h2 style={{ color: "#C9A84C", margin: 0, fontSize: 20, ...GL }}>📊 Funnel — {days} derniers jours</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#7A9BB5", fontSize: 20, cursor: "pointer", padding: 4 }}>✕</button>
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          {[14, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} disabled={busy}
              style={{ fontSize: 12, letterSpacing: ".06em", textTransform: "uppercase", borderRadius: 16, padding: "5px 13px", cursor: busy ? "default" : "pointer",
                color: d === days ? "#07111F" : "#7A9BB5", background: d === days ? "#C9A84C" : "transparent",
                border: "1px solid " + (d === days ? "#C9A84C" : "#1A2F45"), fontWeight: d === days ? 700 : 400, ...GL }}>
              {d === 90 ? "90 j (max)" : d + " j"}
            </button>
          ))}
        </div>

        {busy && <p style={{ ...lab, marginTop: 16 }}>Chargement…</p>}
        {err && <p style={{ color: "#E8534A", fontSize: 14, marginTop: 14, ...GL }}>❌ {err}</p>}

        {cov && (() => {
          const REG_LABEL = {
            carib_east: "Caraïbes Est", carib_west: "Caraïbes Ouest", carib_south: "Caraïbes Sud",
            bermuda: "Bermudes", europe_med: "Méditerranée", europe_north: "Europe Nord",
            europe_british: "Îles Britanniques", alaska: "Alaska", hawaii: "Hawaï", asia: "Asie",
          };
          const r = cov.robot || {};
          const pct = r.combos_total ? Math.round((r.cursor / r.combos_total) * 100) : 0;
          const rows = Object.entries(cov.by_region || {}).sort((a, b) => b[1] - a[1]);
          return (
            <div style={{ marginTop: 14 }}>
              <h3 style={{ color: "#C9A84C", fontSize: 14, margin: "0 0 8px", ...GL }}>🗄️ Cache de voilages — couverture</h3>
              <div style={{ border: "1px solid #1A2F45", borderRadius: 12, padding: "10px 14px", marginBottom: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}><span style={lab}>Voilages confirmés en cache</span><span style={num}>{cov.total_sailings}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}><span style={lab}>Robot — 1er tour</span><span style={num}>{r.cursor}/{r.combos_total} ({pct}%)</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}><span style={lab}>Combos / réveil · file reprise</span><span style={num}>{r.combos_per_run} · {r.retry_queue}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}><span style={lab}>Robot actif</span><span style={num}>{r.enabled ? "✅ oui" : "⏸️ coupé"}</span></div>
              </div>
              <div style={{ border: "1px solid #1A2F45", borderRadius: 12, overflow: "hidden" }}>
                {rows.map(([reg, count], i) => (
                  <div key={reg} style={{ display: "flex", justifyContent: "space-between", padding: "7px 14px", background: i % 2 ? "transparent" : "rgba(201,168,76,0.04)" }}>
                    <span style={lab}>{count >= 8 ? "🟢" : count >= 3 ? "🟡" : "🔴"} {REG_LABEL[reg] || reg}</span>
                    <span style={num}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {data && (
          <>
            <div style={{ marginTop: 14, border: "1px solid #1A2F45", borderRadius: 12, overflow: "hidden" }}>
              {FUNNEL.map(([ev, label], i) => (
                <div key={ev} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: i % 2 ? "transparent" : "rgba(201,168,76,0.04)", borderBottom: i < FUNNEL.length - 1 ? "1px solid #11243A" : "none" }}>
                  <span style={lab}>{label}</span>
                  <span style={num}>{n(ev)}</span>
                </div>
              ))}
            </div>

            {daily.length > 0 && (
              <>
                <h3 style={{ color: "#C9A84C", fontSize: 14, margin: "20px 0 8px", ...GL }}>🔍 Recherches par jour (30 j)</h3>
                <div style={{ border: "1px solid #1A2F45", borderRadius: 12, overflow: "hidden" }}>
                  {daily.map((d, i) => (
                    <div key={d.day} style={{ display: "flex", justifyContent: "space-between", padding: "7px 14px", background: i % 2 ? "transparent" : "rgba(201,168,76,0.04)" }}>
                      <span style={lab}>{d.day}</span>
                      <span style={num}>{Math.round(Number(d.n))}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <h3 style={{ color: "#C9A84C", fontSize: 14, margin: "20px 0 8px", ...GL }}>Tous les events</h3>
            <div style={{ border: "1px solid #1A2F45", borderRadius: 12, overflow: "hidden" }}>
              {totals.map((t, i) => (
                <div key={t.event} style={{ display: "flex", justifyContent: "space-between", padding: "7px 14px", background: i % 2 ? "transparent" : "rgba(201,168,76,0.04)" }}>
                  <span style={{ ...lab, fontFamily: "monospace" }}>{t.event}</span>
                  <span style={num}>{Math.round(Number(t.n))}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── ADMIN: génération de codes testeurs uniques ───────────────────────────────

export function AdminCodesModal({ workerUrl, onClose }) {
  const { getToken } = useAuth();
  const [count, setCount]     = useState(20);
  const [credits, setCredits] = useState(5);
  const [busy, setBusy]       = useState(false);
  const [codes, setCodes]     = useState(null);
  const [err, setErr]         = useState(null);

  async function generate() {
    if (busy) return;
    setBusy(true); setErr(null); setCodes(null);
    try {
      const token = await getToken();
      const res = await fetch(workerUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "generate_codes", count, credits, token }),
      });
      const json = await res.json();
      if (json.ok && Array.isArray(json.codes)) setCodes(json.codes);
      else setErr(json.error?.message || "Échec de génération (admin requis).");
    } catch {
      setErr("Erreur de connexion.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 210, padding: 16 }}>
      <div style={{ background: "#07111F", border: "1px solid #C9A84C33", borderRadius: 20, maxWidth: 460, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <h2 style={{ color: "#C9A84C", margin: 0, fontSize: 20, ...GL }}>🎟️ Codes testeurs</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#7A9BB5", fontSize: 20, cursor: "pointer", padding: 4 }}>✕</button>
        </div>
        <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
          <label style={{ flex: 1, color: "#7A9BB5", fontSize: 14, ...GL }}>
            Nombre
            <input type="number" min={1} max={100} value={count} onChange={e => setCount(parseInt(e.target.value) || 1)}
              style={{ width: "100%", boxSizing: "border-box", marginTop: 4, background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 8, padding: "9px 10px", color: "#C8D8E8", fontSize: 14, ...GL }} />
          </label>
          <label style={{ flex: 1, color: "#7A9BB5", fontSize: 14, ...GL }}>
            Recherches / code
            <input type="number" min={1} max={50} value={credits} onChange={e => setCredits(parseInt(e.target.value) || 1)}
              style={{ width: "100%", boxSizing: "border-box", marginTop: 4, background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 8, padding: "9px 10px", color: "#C8D8E8", fontSize: 14, ...GL }} />
          </label>
        </div>
        <button onClick={generate} disabled={busy} style={{
          width: "100%", padding: "11px", borderRadius: 10, border: "none",
          background: busy ? "#1A2F45" : "linear-gradient(135deg,#C9A84C,#A8882A)",
          color: busy ? "#3A5A72" : "#07111F", fontSize: 14, fontWeight: 700, cursor: busy ? "default" : "pointer", ...GL,
        }}>
          {busy ? "Génération…" : `Générer ${count} codes (${credits} recherches chacun)`}
        </button>
        {err && <p style={{ color: "#E8534A", fontSize: 15, marginTop: 12, ...GL }}>❌ {err}</p>}
        {codes && (
          <>
            <p style={{ color: "#5CB85C", fontSize: 15, margin: "14px 0 6px", ...GL }}>✅ {codes.length} codes — à usage unique. Copiez et distribuez :</p>
            <textarea readOnly value={codes.join("\n")} onFocus={e => e.target.select()}
              style={{ width: "100%", boxSizing: "border-box", height: 180, background: "#0A1E35", border: "1px solid #1A2F45", borderRadius: 10, padding: 12, color: "#C8D8E8", fontSize: 15, fontFamily: "monospace", resize: "vertical" }} />
          </>
        )}
      </div>
    </div>
  );
}
